/*global Faye*/
define('lib/Podio.Push',['require','underscore','jquery','faye','lib/Podio.Event','lib/Podio.Config','lib/Podio.Transport','lib/Podio.Routes','signals'],function(require) {

  var _ = require('underscore');
  var $ = require('jquery');
  var Faye = require('faye');

  var podioEvent = require('lib/Podio.Event');
  var podioConfig = require('lib/Podio.Config');
  var transport = require('lib/Podio.Transport');
  var routes = require('lib/Podio.Routes');
  var signals = require('signals');

  function PodioPush() {

    _.bindAll(this, '_fayeExtensionOutgoing', '_fayeExtensionIncoming', 'onSignatureError', 'onPushConfigLoaded', 'onDomReady', 'onFirstSubscription');

    this.listeners = [];
    this.subscriptionPromises = {};
    this.subscriptionsData = {};
    this.isEnabled = podioConfig.Push ? true : false;
    this.subscriptionsRetryLimit = 3;

    this.onFirstSubscriptionPromise  = new $.Deferred();
    this.onDomReadyPromise  = new $.Deferred();

    // Enable to see what Faye is doing
    // Faye.logger = function(msg) { console.log(msg) };

    if(this.isEnabled) {
      this.initialize();
    }
  }

  _.extend(PodioPush.prototype, {

    signals: {
      onSubscriptionError: new signals.Signal(),
      onSignatureError: new signals.Signal(),
      onTransportDown: new signals.Signal(),
      onTransportUp: new signals.Signal()
    },

    initialize: function() {
      var url = podioConfig.Push.server;

      this.fayeClient = new Faye.Client(url, {timeout: 240, retry: 30});

      // no need to even try websockets, we don't support it
      this.fayeClient.disable('websocket');
      // increase maxRequestSize, otherwise Faye will chunk the subscribe call into a lot of small requests
      this.fayeClient._dispatcher.maxRequestSize = 1000000;

      this.fayeClient.addExtension({
        outgoing: this._fayeExtensionOutgoing,
        incoming: this._fayeExtensionIncoming
      });

      // Event handlers
      this.fayeClient.bind('transport:up', this.signals.onTransportUp.dispatch);
      this.fayeClient.bind('transport:down', this.signals.onTransportDown.dispatch);
      this.signals.onSignatureError.add(this.onSignatureError);
      podioEvent.bind(podioEvent.Types.init, this.onDomReady);

      // Wait for both FirstSubscription and DomReady, before connecting to push, to avoid blocking of page load, by prolonging the domReady event.
      $.when(this.onFirstSubscriptionPromise, this.onDomReadyPromise).then(function(context) {
        context.connect(podioConfig.Push.subscriptions);
      });

    },

    connect: function(subscriptions) {
      _.each(subscriptions, function(channel) {
        this.subscribeToChannel(channel);
      }, this);
    },

    dispose: function() {
      _.each(this.subscriptionsData, function(channel) {
        this.unsubscribeFromChannel(channel);
      }, this);

      this.fayeClient.disconnect();

      this.onFirstSubscriptionPromise = new $.Deferred();
    },

    subscribeToChannel: function(channelData) {
      if(!this.isEnabled) {
        return;
      }

      var name = channelData.channel;

      // Ensure we don't have double subscriptions
      if(!this.subscriptionsData[name]) {
        this.subscriptionsData[name] = channelData;
        this.subscriptionsData[name].subscription = this.fayeClient.subscribe(name, _.bind(this._onResponse, this));

        var context = this;

        this.subscriptionsData[name].subscription.callback(function() {
          context.onPushReady(name).resolve();
        });
      }
    },

    unsubscribeFromChannel: function(channelData) {
      if(!this.isEnabled) {
        return;
      }

      var name = channelData.channel;
      if(this.subscriptionsData[name]) {
        this.subscriptionsData[name].subscription.cancel();
        delete this.subscriptionsData[name];
        delete this.subscriptionPromises[name];
      }
    },

    off: function(ref, eventNames, callback) {
      if(!this.isEnabled) {
        return;
      }

      if (_.isString(ref)) {
        ref = {
          type: ref
        };
      }

      this._bindingValidator(ref, eventNames, callback);

      if(_.isArray(eventNames)) {

        _.each(eventNames, function(e) {
          this.off(ref, e, callback);
        }, this);

      } else {

        this.listeners = _.reject(this.listeners, function(listener) {
          return (ref.type === listener.ref.type &&
                  (_.isUndefined(ref.id) || ref.id === listener.ref.id) &&
                  eventNames === listener.event &&
                  callback === listener.callback);
        }, this);

      }
    },

    on: function(ref, eventNames, callback, ignoreOwn) {
      if(!this.isEnabled) {
        return;
      }

      if (_.isString(ref)) {
        ref = {
          type: ref
        };
      }

      this.onFirstSubscription(); // Bound as _.once, to only execute once;

      ignoreOwn = ignoreOwn || false;

      this._bindingValidator(ref, eventNames, callback);

      if(_.isArray(eventNames)) {

        _.each(eventNames, function(e) {
          this.on(ref, e, callback, ignoreOwn);
        }, this);

      } else {

        var listener = {
          'ref'       : ref,
          'event'     : eventNames,
          'callback'  : callback,
          'ignoreOwn' : ignoreOwn
        };

        this.listeners.push(listener);
      }
    },

    onPushReady: function(name) {
      if (!this.subscriptionPromises[name]) {
        this.subscriptionPromises[name] = $.Deferred();
      }

      return this.subscriptionPromises[name];
    },

    onSubscriptionError: function(error) {
      this.signals.onSubscriptionError.dispatch(error);

      if(error.code === 400) {
        this.signals.onSignatureError.dispatch(error);
      }
    },

    onSignatureError: function() {
      if(this._subscriptionRetryCount >= this.subscriptionsRetryLimit) {
        return;
      }

      var context = this;
      var url = routes.push_config_path();
      var channelNames = _.pluck(this.subscriptionsData, 'channel');

      var req = transport.postJSON(url, { requestData: { channels: channelNames }});

      req.done(function(responseData) {
        var config = responseData.data.config;
        context.onPushConfigLoaded(config);
      });

      if(_.isNumber(this._subscriptionRetryCount)) {
        this._subscriptionRetryCount += 1;
      } else {
        this._subscriptionRetryCount = 1;
      }
    },

    onPushConfigLoaded: function(config) {
      this.dispose();
      this.initialize();
      this.connect(config.subscriptions);
    },

    onFirstSubscription: _.once(function() {
      this.onFirstSubscriptionPromise.resolve(this);
    }),

    onDomReady: function() {
      this.onDomReadyPromise.resolve(this);
    },

    _fayeExtensionOutgoing: function(message, callback) {
      if (message.channel == "/meta/subscribe") {
        // Attach the signature and timestamp to subscription messages
        var subscription = this.subscriptionsData[message.subscription];

        if (!message.ext) {
          message.ext = {};
        }

        if(subscription) {
          message.ext.private_pub_signature = subscription.signature;
          message.ext.private_pub_timestamp = subscription.timestamp;
        }

      }

      callback(message);
    },

    _fayeExtensionIncoming: function(message, callback) {
      if (message.channel == "/meta/subscribe" && message.successful === false) {
        this.onSubscriptionError.apply(this, [Faye.Error.parse(message.error)]);
      }

      callback(message);
    },

    _bindingValidator: function(ref, eventNames, callback) {
      if(!ref.type) {
        throw ('ref.type is needed');
      }

      if(!callback) {
        throw ('callback is needed');
      }
    },

    _onResponse: function(message) {

      var isOwn = message.data.created_by['type'] === 'user' &&
                  message.data.created_by['id'] === podioConfig.user.user_id &&
                  (message.data.created_via === 1 || message.data.created_via === null);

      _.each(this.listeners, function(listener) {
        if(message.data.ref.type === listener.ref.type &&
           (_.isUndefined(listener.ref.id) || message.data.ref.id === listener.ref.id) &&
           message.data.event === listener.event &&
           !(isOwn && listener.ignoreOwn)) {

           listener.callback(message.data.data);
        }

      }, this);
    }

  });

  return new PodioPush();

});

